import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import Layout from "@/components/Layout";
import api from "../../services/api";
import { API_ENDPOINTS } from "../../config/api";
import { GAME_LIST } from "../../utils/gameList";

export default function BetList({ userId, staffId, embedded = false, isPadding = true }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  // const [eventCategory, setEventCategory] = useState("All"); // Removed separate category state
  const [eventFilter, setEventFilter] = useState("All");
  const [marketFilter, setMarketFilter] = useState("All");
  const [rateFilter, setRateFilter] = useState("All");
  const [amountFilter, setAmountFilter] = useState("All");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [betStatus, setBetStatus] = useState("Current");
  const [betMatching, setBetMatching] = useState("Matched");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [betData, setBetData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recommendations, setRecommendations] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchBets = async (statusOverride = null) => {
    setLoading(true);
    try {
      const currentStatus = statusOverride || betStatus;

      // Derive category based on selected event
      let selectedCategory = undefined;
      let selectedSport = undefined;

      if (eventFilter !== 'All') {
        const game = GAME_LIST.find(g => g.value === eventFilter);
        if (game) {
          selectedCategory = game.category; // 'sport' or 'casino'
          selectedSport = eventFilter; // '4' or 'teen'
        } else {
          // fallback if it was a custom value?
          selectedSport = eventFilter;
        }
      }

      const endpoint = (userId || staffId) ? '/lords/bet-list-by-user' : '/lords/bet-list';

      const response = await api.get(endpoint, {
        params: {
          type: currentStatus.toLowerCase(),
          from: fromDate,
          to: toDate,
          page: currentPage,
          limit: entriesPerPage,
          search: tableSearchTerm || undefined,
          username: searchTerm || undefined,
          category: selectedCategory, // Send 'sport' or 'casino' or undefined
          sport: selectedSport, // Send '4', 'teen', etc.
          status: currentStatus === 'Current' ? betMatching : undefined,
          userId: userId || undefined,
          staffId: staffId || undefined
        }
      });

      if (response.data && response.data.success) {
        setBetData(response.data.data);
        setTotalRecords(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching bets:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBets();
  }, [currentPage, entriesPerPage]); // Only fetch on page/limit changes, not on filter changes

  // Debounced User Search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        fetchUserSuggestions(searchTerm);
      } else {
        setRecommendations([]);
        setShowDropdown(false);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUserSuggestions = async (term) => {
    setSearchLoading(true);
    setShowDropdown(true);
    setRecommendations([]);
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH_BY_USERNAME, {
        params: { username: term }
      });
      if (response.data?.data?.results && Array.isArray(response.data.data.results)) {
        setRecommendations(response.data.data.results);
      } else {
        setRecommendations([]);
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error);
      setRecommendations([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectUser = (username) => {
    setSearchTerm(username);
    setShowDropdown(false);
    setRecommendations([]);
  };


  const filteredBets = useMemo(() => {
    // Client-side mapping or additional filtering if needed
    return betData.map(bet => ({
      id: bet.id,
      placeDate: new Date(bet.created_at).toLocaleString(),
      description: bet.description ? bet.description + (bet.selection ? ` - ${bet.selection}` : '') : (bet.match_title ? `${bet.match_title} - ${bet.selection_name}` : '-'),
      userName: bet.user ? bet.user.username : (bet.username || bet.user_id || '-'),
      betType: bet.bet_type || bet.type || bet.game_type || '-',
      userRate: bet.odds ? parseFloat(bet.odds).toFixed(2) : '-',
      winLoss: bet.result_status || bet.status || '-',
      ip: bet.ip_address || '-',
      browserDetails: 'Detail',
      amount: bet.amount ? parseFloat(bet.amount) : (bet.stake_amount ? parseFloat(bet.stake_amount) : 0),
      source: bet.source || 'sport' // useful for UI indication if needed
    }));
  }, [betData]);

  const totalPages = Math.ceil(totalRecords / entriesPerPage);

  const handleApply = () => {
    setCurrentPage(1); // Reset to first page on new filter
    fetchBets();
  };

  const handleCancel = () => {
    setEventFilter("All");
    setMarketFilter("All");
    setRateFilter("All");
    setAmountFilter("All");
    setSearchTerm("");
    setTableSearchTerm("");
    setFromDate("2024-01-01");
    setToDate("2025-12-31");
  };


  const handleDownloadCSV = () => {
    console.log("Downloading CSV...");
    // TODO: Implement CSV download
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleBrowserDetails = (betId) => {
    console.log("Viewing browser details for bet:", betId);
    // TODO: Implement browser details modal
  };

  const content = (
    <div className={`${isPadding ? 'p-4' : ''} w-full mx-auto bg-[#171b36] min-h-screen ${embedded ? '' : ''}`}>
      {/* Header Section */}
      <div
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
        className="shadow-sm px-4 pt-4"
      >
        <div className="flex justify-between items-start mb-2">
          {!embedded && (
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Bet List
            </div>
          )}
          <div className={`flex items-center gap-2 ${embedded ? 'w-full justify-between' : ''}`}>
            {embedded && <div className="text-white font-bold text-lg">Bet List</div>}
            <button
              onClick={handleDownloadCSV}
              className="bg-[#333d79ff] hover:bg-[#1a4f66] text-white font-bold text-[14px] px-3 py-2 transition-colors ml-auto"
            >
              Download CSV
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-white mb-2"></div>

      </div>

      {/* Status and Display Controls */}
      <div
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-10px" }}
        className="shadow-sm px-4 py-2 "
      >
        {/* Filter Section - Row 1 */}
        <div className="flex  items-center gap-2 mb-2">
          <div style={{ width: "20%" }} className="flex flex-col gap-2">
            <label className="text-white text-[12px] font-bold">Event</label>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="All">All</option>
              {GAME_LIST.map((game, index) => (
                <option key={`${game.value}-${index}`} value={game.value}>
                  {game.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ width: "20%" }} className="flex flex-col gap-2">

            <label className="text-white text-[12px] font-bold">Market:</label>
            <select
              value={marketFilter}
              onChange={(e) => setMarketFilter(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="All">All</option>
            </select>
          </div>
          <div style={{ width: "10%" }} className="flex flex-col gap-2">

            <label className="text-white text-[12px] font-bold">Rate</label>
            <select
              value={rateFilter}
              onChange={(e) => setRateFilter(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="All">Odds: All</option>
            </select>
          </div>

          <div style={{ width: "10%" }} className="flex flex-col gap-2">
            <label className="text-white text-[12px] font-bold">Amount</label>
            <select
              value={amountFilter}
              onChange={(e) => setAmountFilter(e.target.value)}
              className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="All">Stake: All</option>
            </select>
          </div>

          <div style={{ width: "10%" }} className="flex flex-col gap-2 relative">
            <label className="text-white text-[12px] font-bold">Search by user</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(false);
              }}
              placeholder="Enter Atleast 3 character"
              className="px-2 py-2 w-[180px] text-[11px] bg-white text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {showDropdown && (
              <ul className="absolute top-full left-0 w-[180px] bg-white text-black text-[12px] rounded-sm shadow-lg max-h-40 overflow-y-auto z-50">
                {searchLoading ? (
                  <li className="px-2 py-2 text-gray-500 italic">Searching...</li>
                ) : recommendations.length > 0 ? (
                  recommendations.map((user, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectUser(user.username)}
                      className="px-2 py-2 cursor-pointer hover:bg-blue-100"
                    >
                      {user.username}
                    </li>
                  ))
                ) : (
                  <li className="px-2 py-2 text-gray-500 italic">No record found</li>
                )}
              </ul>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 w-full justify-end">
          <button
            onClick={handleApply}
            className="bg-[#11374a]  hover:bg-[#1a4f66] text-white text-[12px] font-bold px-3 py-2 transition-colors"
          >
            {loading ? "Loading..." : "Apply"}
          </button>
          <button
            onClick={handleCancel}
            className="bg-[#b71601] hover:bg-red-600 text-white text-[12px] font-bold px-3 py-2 transition-colors"
          >
            Cancel
          </button>
        </div>


        <div className="flex flex-col gap-2 mb-2">
          {
            betStatus === "Past" && (
              <div className="flex items-center gap-2">
                <div style={{ width: "16%" }} className="flex flex-col gap-2">
                  <label className="text-white text-[12px] font-bold">From:</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
                  />
                </div>

                <div style={{ width: "16%" }} className="flex flex-col gap-2">
                  <label className="text-white text-[12px] font-bold">To:</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
                  />
                </div>
              </div>
            )
          }
          <div style={{ width: "100%" }} className="flex flex-col gap-2 pb-4 ">
            {/* Bet Status Tabs */}
            <div className="flex">
              <button
                onClick={() => {
                  setBetStatus("Current");
                  setCurrentPage(1);
                  fetchBets("Current");
                }}
                className={`px-4 py-2 text-[12px] font-bold transition-colors ${betStatus === "Current"
                  ? "bg-[#333d79ff] text-white"
                  : "bg-gray-600 text-white"
                  }`}
              >
                Current
              </button>
              <button
                onClick={() => {
                  setBetStatus("Past");
                  setCurrentPage(1);
                  fetchBets("Past");
                }}
                className={`px-4 py-2 text-[12px] font-bold transition-colors ${betStatus === "Past"
                  ? "bg-[#333d79ff] text-white"
                  : "bg-gray-600 text-white"
                  }`}
              >
                Past
              </button>
            </div>
          </div>


        </div>
        <div className="flex flex-wrap items-center gap-4 mb-2">


          {/* Bet Matching Radio Buttons */}
          {betStatus === "Current" && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="betMatching"
                  value="Matched"
                  checked={betMatching === "Matched"}
                  onChange={(e) => setBetMatching(e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: "#3cd247" }}
                />
                <span className="text-white text-[12px] font-bold">Matched</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="betMatching"
                  value="Unmatched"
                  checked={betMatching === "Unmatched"}
                  onChange={(e) => setBetMatching(e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: "#3cd247" }}
                />
                <span className="text-white text-[12px] font-bold">Unmatched</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="betMatching"
                  value="Deleted"
                  checked={betMatching === "Deleted"}
                  onChange={(e) => setBetMatching(e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: "#3cd247" }}
                />
                <span className="text-white text-[12px] font-bold">Deleted</span>
              </label>
            </div>
          )}

          {/* Entries Display */}
          <div className="flex items-center gap-2">
            <span className="text-white text-[12px] font-bold">Show</span>
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="text-white text-[12px] font-bold">entries</span>
          </div>

          {/* Search within Table */}
          <div className="flex items-center gap-2 ml-auto">
            <label className="text-white text-[12px] font-bold">Search:</label>
            <input
              type="text"
              value={tableSearchTerm}
              onChange={(e) => setTableSearchTerm(e.target.value)}
              placeholder="Type to Search"
              className="px-2 py-1 text-[12px] bg-white text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", }}
        className="shadow-sm px-4 py-2"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                  Place Date
                </th>
                <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                  Description
                </th>
                <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                  User name
                </th>
                <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                  Bet Type
                </th>
                <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                  User Rate
                </th>
                <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                  Win/Loss
                </th>
                <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                  IP
                </th>
                <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                  Browser Details
                </th>
                <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredBets.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-2 py-8 text-center text-gray-400">
                    There are no records to show
                  </td>
                </tr>
              ) : (
                filteredBets.map((bet, index) => (
                  <tr
                    key={bet.id}
                    className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                      }`}
                  >
                    <td className="py-1 px-2 text-white text-[13px]">
                      {bet.placeDate}
                    </td>
                    <td className="py-1 px-2 text-white font-medium">
                      {bet.description}
                    </td>
                    <td className="py-1 px-2 text-white font-medium">
                      {bet.userName}
                    </td>
                    <td className="py-1 px-2 text-center text-white font-bold">
                      {bet.betType}
                    </td>
                    <td className="py-1 px-2 text-center text-white font-bold">
                      {bet.userRate}
                    </td>
                    <td className="py-1 px-2 text-center text-white font-bold">
                      {bet.winLoss}
                    </td>
                    <td className="py-1 px-2 text-center text-white text-[13px]">
                      {bet.ip}
                    </td>
                    <td className="py-1 px-2 text-center">
                      <button
                        onClick={() => handleBrowserDetails(bet.id)}
                        className="text-white hover:text-blue-300 transition-colors flex items-center gap-1 mx-auto"
                      >
                        <span className="text-[12px]">{bet.browserDetails}</span>
                        <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                      </button>
                    </td>
                    <td className="py-1 px-2 text-center text-white font-bold">
                      {bet.amount.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-end items-center gap-2 mt-4">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 text-[14px] font-bold transition-colors ${currentPage === page
                ? "bg-[#333d79ff] text-white"
                : "text-white hover:bg-gray-700"
                }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
}

